import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AnalyticsPeriod } from '../../models/analytics.types';
import { EXPORT_FORMATS, ExportFormat } from '../../models/period-trends.const';
import {
  ProfessorAnalyticsService,
  ReportsExportAnalyticsResponse,
  ReportScheduleRow,
  ReportScheduleFrequency,
  ReportShareSummary,
} from '../../services/professor-analytics.service';

@Component({
  selector: 'tangram-pa-reports-export-tab',
  templateUrl: './reports-export-tab.component.html',
  styleUrls: ['./reports-export-tab.component.scss']
})
export class ReportsExportTabComponent implements OnInit, OnChanges {
  // Period flows down from the parent dashboard so every tab speaks the
  // same period vocabulary. Custom-range start/end ride alongside.
  @Input() selectedPeriod: AnalyticsPeriod = 'This Month';
  @Input() startDate = '';
  @Input() endDate   = '';

  // Bubble the CSV download up to the parent dashboard, which already owns
  // the per-tab CSV builders + browser download plumbing.
  @Output() downloadCsv = new EventEmitter<void>();

  loading = true;
  error = '';

  reportSchedules: ReportScheduleRow[] = [];
  shareSummary: ReportShareSummary | null = null;

  // Local export controls — the selector and Download button at the
  // bottom of the tab. The dashboard-level Export button still works;
  // this duplicate lives here per the design so the user can pick a
  // format and download without leaving the tab.
  exportFormats = EXPORT_FORMATS;
  selectedExportFormat: ExportFormat = 'CSV';

  // Per-frequency in-flight flag so the toggle UI can show "saving" /
  // disable the button while the PATCH round-trips. Optimistic updates
  // are applied immediately and rolled back on failure.
  togglingFrequencies = new Set<ReportScheduleFrequency>();

  // Cancel an in-flight GET before starting a new one so a fast period
  // switch can't overwrite fresh data with stale.
  private fetchSub?: Subscription;

  constructor(private analyticsService: ProfessorAnalyticsService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['selectedPeriod'] || changes['startDate'] || changes['endDate']) &&
        !(changes['selectedPeriod'] && changes['selectedPeriod'].firstChange)) {
      this.load();
    }
  }

  toggleSchedule(schedule: ReportScheduleRow): void {
    if (this.togglingFrequencies.has(schedule.frequency)) { return; }
    const next = !schedule.enabled;
    const previous = schedule.enabled;

    this.reportSchedules = this.reportSchedules.map((s) =>
      s.frequency === schedule.frequency ? { ...s, enabled: next } : s
    );
    this.togglingFrequencies.add(schedule.frequency);

    this.analyticsService
      .updateReportSchedule(schedule.frequency, next)
      .subscribe(
        () => {
          this.togglingFrequencies.delete(schedule.frequency);
        },
        () => {
          this.togglingFrequencies.delete(schedule.frequency);
          // Roll the optimistic update back so the UI doesn't lie about
          // the persisted state when the PATCH fails.
          this.reportSchedules = this.reportSchedules.map((s) =>
            s.frequency === schedule.frequency ? { ...s, enabled: previous } : s
          );
          this.error = 'Failed to save preference. Try again.';
        },
      );
  }

  async exportInsights(): Promise<void> {
    if (!this.reportSchedules.length && !this.shareSummary) { return; }

    const periodSlug = String(this.selectedPeriod || 'period').replace(/\s+/g, '-').toLowerCase();
    const baseName = `analytics-reports-export-${periodSlug}`;

    if (this.selectedExportFormat === 'PDF') {
      // Rasterize the brand SVG so the zero-dep PDF builder can embed it as a
      // DCTDecode XObject. A logo failure must not block the export — fall
      // back to a logo-less PDF.
      const logo = await this.loadLogoJpegBytes().catch(() => null);
      const pdfBytes = this.buildPdf(this.buildReportRows(), logo);
      this.triggerDownload(new Blob([pdfBytes], { type: 'application/pdf' }), `${baseName}.pdf`);
      return;
    }

    if (this.selectedExportFormat === 'Excel') {
      const html = this.buildExcelHtml();
      // Excel opens an HTML table sent with the .xls MIME type cleanly.
      // The BOM keeps non-ASCII labels (em-dashes, accents) intact.
      const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
      this.triggerDownload(new Blob([bom, html], { type: 'application/vnd.ms-excel' }), `${baseName}.xls`);
      return;
    }

    const csv = this.buildCsv();
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
    this.triggerDownload(new Blob([bom, csv], { type: 'text/csv;charset=utf-8;' }), `${baseName}.csv`);
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Fetch the Tangramio SVG logo and rasterize it to a JPEG byte buffer the
  // PDF builder can embed as a DCTDecode Image XObject. JPEG is chosen over
  // PNG because PDFs accept JPEG streams verbatim — no zlib path needed.
  // Drawn at ~4x display size to stay crisp; flattened onto white because
  // JPEG has no alpha channel.
  private async loadLogoJpegBytes(): Promise<{ bytes: Uint8Array; width: number; height: number } | null> {
    if (typeof document === 'undefined' || typeof fetch === 'undefined') { return null; }
    const response = await fetch('/assets/img/logo.svg');
    if (!response.ok) { return null; }
    const svgText = await response.text();
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
    const svgUrl = URL.createObjectURL(svgBlob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error('logo image failed to load'));
        i.src = svgUrl;
      });
      const targetW = 970;
      const intrinsicRatio = (img.naturalHeight || 70) / (img.naturalWidth || 242.516);
      const targetH = Math.max(1, Math.round(targetW * intrinsicRatio));
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) { return null; }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetW, targetH);
      ctx.drawImage(img, 0, 0, targetW, targetH);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const base64 = dataUrl.split(',')[1] || '';
      if (!base64) { return null; }
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) { bytes[i] = binary.charCodeAt(i); }
      return { bytes, width: targetW, height: targetH };
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  }

  // Canonical row list shared by every exporter. Each entry is either a
  // section header (single string) or a [label, ...values] tuple. CSV /
  // Excel / PDF builders read this and render in their native shape so a
  // change to the report shape only happens in one place.
  private buildReportRows(): Array<string | string[]> {
    const rows: Array<string | string[]> = [];
    rows.push(['Tab', 'Reports & Export']);
    rows.push(['Period', String(this.selectedPeriod)]);
    rows.push(['Generated', new Date().toISOString()]);
    rows.push('');
    rows.push('Scheduled Email Reports');
    rows.push(['Frequency', 'Recipients', 'Next Send', 'Enabled']);
    (this.reportSchedules || []).forEach((row) => {
      rows.push([
        String(row.frequency),
        String(row.recipients),
        String(row.nextSend),
        row.enabled ? 'Yes' : 'No',
      ]);
    });
    if (this.shareSummary) {
      rows.push('');
      rows.push('Share Summary');
      rows.push(['Metric', 'Value']);
      rows.push(['Period', this.shareSummary.periodLabel]);
      rows.push(['Published Tangrams', String(this.shareSummary.publishedTangrams)]);
      rows.push(['Total Views', String(this.shareSummary.totalViews)]);
      rows.push(['Unique Viewers', String(this.shareSummary.uniqueStudents)]);
      rows.push(['Avg Completion Rate', `${this.shareSummary.avgCompletionRate}%`]);
      rows.push(['Avg Time Spent', this.shareSummary.avgTimeSpent]);
    }
    return rows;
  }

  private buildCsv(): string {
    const rows: Array<string | string[]> = [
      ['tangramio'],
      '',
      ...this.buildReportRows(),
    ];
    return rows.map((row) => {
      if (typeof row === 'string') { return row; }
      return row.map((cell) => this.csvCell(cell)).join(',');
    }).join('\n');
  }

  private buildExcelHtml(): string {
    const esc = (s: string) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    const rows: Array<string | string[]> = [
      ['tangramio'],
      '',
      ...this.buildReportRows(),
    ];
    const body = rows.map((row) => {
      if (typeof row === 'string') {
        // Section header / blank line — span 4 cols so it visually breaks.
        if (!row) { return '<tr><td colspan="4">&nbsp;</td></tr>'; }
        return `<tr><td colspan="4"><b>${esc(row)}</b></td></tr>`;
      }
      return '<tr>' + row.map((c) => `<td>${esc(c)}</td>`).join('') + '</tr>';
    }).join('');
    return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="UTF-8"></head><body><table border="1">${body}</table></body></html>`;
  }

  // Bordered-table PDF builder. Produces a valid PDF/1.4 document with
  // Helvetica + Helvetica-Bold, paginated automatically. Tabular rows
  // are laid out into cells with grid lines so columns line up cleanly
  // (mirroring the Excel export). Avoids adding a runtime dependency.
  private buildPdf(
    rows: Array<string | string[]>,
    logo: { bytes: Uint8Array; width: number; height: number } | null = null,
  ): Uint8Array {
    const PAGE_W = 612, PAGE_H = 792;
    const MARGIN = 50;
    const FONT_SIZE = 10;
    const HEADER_FONT_SIZE = 12;
    const ROW_HEIGHT = 18;
    const HEADER_HEIGHT = 22;
    const SPACER_HEIGHT = 8;
    const CELL_PAD_X = 4;
    const USABLE_W = PAGE_W - 2 * MARGIN;
    const USABLE_H = PAGE_H - 2 * MARGIN;

    // Branding: reserve space at the top of page 1 for the Tangramio logo.
    // Width is fixed in PDF points; height is derived from the rasterized
    // image's aspect ratio so the wordmark never stretches.
    const hasLogo = !!logo;
    const LOGO_W_PT = hasLogo ? 130 : 0;
    const LOGO_H_PT = hasLogo ? Math.max(1, LOGO_W_PT * (logo!.height / logo!.width)) : 0;
    const LOGO_GAP_PT = hasLogo ? 14 : 0;
    const FIRST_PAGE_TOP_OFFSET = LOGO_H_PT + LOGO_GAP_PT;

    // Strip non-Latin1 characters — Helvetica's default encoding doesn't
    // cover them and would render as garbage. Replace with '?'.
    const sanitize = (s: string) => String(s == null ? '' : s).replace(/[^\x20-\x7E]/g, '?');
    const escapePdfString = (s: string) =>
      s.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
    // Approx Helvetica advance width — good enough for cell-fit truncation.
    const approxWidth = (s: string, size: number) => s.length * size * 0.5;
    const truncate = (s: string, maxWidth: number, size: number): string => {
      if (maxWidth <= 0) { return ''; }
      if (approxWidth(s, size) <= maxWidth) { return s; }
      const ellipsis = '...';
      const ellipsisW = approxWidth(ellipsis, size);
      let lo = 0, hi = s.length;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (approxWidth(s.slice(0, mid), size) + ellipsisW <= maxWidth) { lo = mid; }
        else { hi = mid - 1; }
      }
      return s.slice(0, lo) + ellipsis;
    };

    // ── Group rows into blocks (section header / spacer / table) ──
    // Consecutive array rows with the same column count form one table;
    // the first row of each table is rendered as a bold, shaded header.
    type Block =
      | { kind: 'header'; text: string }
      | { kind: 'spacer' }
      | { kind: 'table'; rows: string[][] };
    const blocks: Block[] = [];
    let tableBuf: string[][] = [];
    const flushTable = () => {
      if (tableBuf.length) {
        blocks.push({ kind: 'table', rows: tableBuf });
        tableBuf = [];
      }
    };
    for (const r of rows) {
      if (typeof r === 'string') {
        flushTable();
        if (!r) { blocks.push({ kind: 'spacer' }); }
        else { blocks.push({ kind: 'header', text: r }); }
      } else {
        if (tableBuf.length && tableBuf[0].length !== r.length) { flushTable(); }
        tableBuf.push(r);
      }
    }
    flushTable();

    // ── Paginate ──
    // y is measured downward from the top of the usable area; converted
    // to PDF coords (origin bottom-left) at render time.
    type Op =
      | { type: 'header'; y: number; text: string }
      | { type: 'table-row'; y: number; cells: string[]; isHeader: boolean; cols: number[] };
    const pages: Op[][] = [];
    let curPage: Op[] = [];
    // First page starts below the logo; later pages start at the top.
    let curY = FIRST_PAGE_TOP_OFFSET;
    const newPage = () => { pages.push(curPage); curPage = []; curY = 0; };
    for (const block of blocks) {
      if (block.kind === 'spacer') {
        curY = Math.min(curY + SPACER_HEIGHT, USABLE_H);
        continue;
      }
      if (block.kind === 'header') {
        if (curY + HEADER_HEIGHT > USABLE_H) { newPage(); }
        curPage.push({ type: 'header', y: curY, text: block.text });
        curY += HEADER_HEIGHT;
        continue;
      }
      const cols = block.rows[0].length;
      const colW = USABLE_W / cols;
      const colXs: number[] = [];
      for (let i = 0; i <= cols; i++) { colXs.push(i * colW); }
      const headerCells = block.rows[0];
      block.rows.forEach((cells, idx) => {
        if (curY + ROW_HEIGHT > USABLE_H) {
          newPage();
          // Re-emit the column header on continued pages so the table
          // remains readable across breaks.
          if (idx > 0) {
            curPage.push({ type: 'table-row', y: curY, cells: headerCells, isHeader: true, cols: colXs });
            curY += ROW_HEIGHT;
          }
        }
        curPage.push({ type: 'table-row', y: curY, cells, isHeader: idx === 0, cols: colXs });
        curY += ROW_HEIGHT;
      });
    }
    if (curPage.length || !pages.length) { pages.push(curPage); }

    const N = pages.length;
    const fontObjId = 3 + N;
    const fontBoldObjId = fontObjId + 1;
    const logoObjId = hasLogo ? fontBoldObjId + 1 : 0;
    const firstContentObjId = (hasLogo ? logoObjId : fontBoldObjId) + 1;

    // Object entries: a plain string is a dictionary-only object; the binary
    // shape carries a raw byte stream (used for the JPEG XObject).
    type PdfObj = string | { dict: string; binary: Uint8Array };
    const objects: PdfObj[] = [];
    objects.push(`<< /Type /Catalog /Pages 2 0 R >>`);
    const kids = pages.map((_, i) => `${3 + i} 0 R`).join(' ');
    objects.push(`<< /Type /Pages /Kids [${kids}] /Count ${N} >>`);
    const resourcesDict = hasLogo
      ? `/Resources << /Font << /F1 ${fontObjId} 0 R /F2 ${fontBoldObjId} 0 R >> /XObject << /Logo ${logoObjId} 0 R >> >>`
      : `/Resources << /Font << /F1 ${fontObjId} 0 R /F2 ${fontBoldObjId} 0 R >> >>`;
    pages.forEach((_, i) => {
      objects.push(
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] ` +
        `${resourcesDict} ` +
        `/Contents ${firstContentObjId + i} 0 R >>`,
      );
    });
    objects.push(`<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>`);
    objects.push(`<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>`);
    if (hasLogo) {
      objects.push({
        dict:
          `<< /Type /XObject /Subtype /Image /Width ${logo!.width} /Height ${logo!.height} ` +
          `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${logo!.bytes.length} >>`,
        binary: logo!.bytes,
      });
    }

    const contentStreams: string[] = pages.map((ops, pageIdx) => {
      const out: string[] = [];
      const toPdfY = (yFromTop: number) => PAGE_H - MARGIN - yFromTop;

      // Draw the brand logo at the top of page 1 only — the rest of the
      // pages keep the full usable area for tabular content.
      if (hasLogo && pageIdx === 0) {
        const x = MARGIN;
        const y = PAGE_H - MARGIN - LOGO_H_PT;
        out.push('q');
        out.push(`${LOGO_W_PT} 0 0 ${LOGO_H_PT} ${x} ${y} cm`);
        out.push('/Logo Do');
        out.push('Q');
      }

      for (const op of ops) {
        if (op.type === 'header') {
          const safe = escapePdfString(sanitize(op.text));
          const yBaseline = toPdfY(op.y + HEADER_HEIGHT - 6);
          out.push('BT');
          out.push(`/F2 ${HEADER_FONT_SIZE} Tf`);
          out.push(`1 0 0 1 ${MARGIN} ${yBaseline} Tm`);
          out.push(`(${safe}) Tj`);
          out.push('ET');
          continue;
        }
        // table row
        const yTop = toPdfY(op.y);
        const yBot = toPdfY(op.y + ROW_HEIGHT);
        const x0 = MARGIN + op.cols[0];
        const x1 = MARGIN + op.cols[op.cols.length - 1];

        if (op.isHeader) {
          // light gray fill behind header row
          out.push('0.92 0.92 0.92 rg');
          out.push(`${x0} ${yBot} ${x1 - x0} ${yTop - yBot} re f`);
          out.push('0 0 0 rg');
        }
        // grid lines
        out.push('0.5 w');
        out.push('0.7 0.7 0.7 RG');
        for (let i = 0; i < op.cols.length; i++) {
          const x = MARGIN + op.cols[i];
          out.push(`${x} ${yTop} m ${x} ${yBot} l S`);
        }
        out.push(`${x0} ${yTop} m ${x1} ${yTop} l S`);
        out.push(`${x0} ${yBot} m ${x1} ${yBot} l S`);
        out.push('0 0 0 RG');

        // cell text
        const font = op.isHeader ? 'F2' : 'F1';
        const yBaseline = toPdfY(op.y + ROW_HEIGHT - 5);
        out.push('BT');
        out.push(`/${font} ${FONT_SIZE} Tf`);
        for (let i = 0; i < op.cells.length; i++) {
          const maxW = (op.cols[i + 1] - op.cols[i]) - 2 * CELL_PAD_X;
          const txt = truncate(sanitize(op.cells[i]), maxW, FONT_SIZE);
          const safe = escapePdfString(txt);
          const x = MARGIN + op.cols[i] + CELL_PAD_X;
          out.push(`1 0 0 1 ${x} ${yBaseline} Tm`);
          out.push(`(${safe}) Tj`);
        }
        out.push('ET');
      }
      return out.join('\n');
    });

    contentStreams.forEach((stream) => {
      // /Length is the byte length of the stream data — ASCII-only after
      // sanitize() so .length is byte-accurate.
      objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    });

    // Assemble byte-by-byte so xref offsets are exact.
    const encoder = new TextEncoder();
    const headerBytes = encoder.encode('%PDF-1.4\n');
    const chunks: Uint8Array[] = [headerBytes];
    let offset = headerBytes.length;
    const objOffsets: number[] = [];
    objects.forEach((entry, i) => {
      objOffsets.push(offset);
      const prefix = encoder.encode(`${i + 1} 0 obj\n`);
      chunks.push(prefix);
      offset += prefix.length;
      if (typeof entry === 'string') {
        const body = encoder.encode(`${entry}\nendobj\n`);
        chunks.push(body);
        offset += body.length;
      } else {
        // Binary stream object (e.g. JPEG XObject): the dictionary and
        // stream/endstream wrappers stay ASCII, but the payload bytes are
        // emitted verbatim so DCTDecode sees an intact JPEG.
        const head = encoder.encode(`${entry.dict}\nstream\n`);
        const tail = encoder.encode(`\nendstream\nendobj\n`);
        chunks.push(head);
        chunks.push(entry.binary);
        chunks.push(tail);
        offset += head.length + entry.binary.length + tail.length;
      }
    });

    const xrefOffset = offset;
    let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
    objOffsets.forEach((off) => {
      const padded = String(off);
      xref += `${'0'.repeat(10 - padded.length)}${padded} 00000 n \n`;
    });
    chunks.push(encoder.encode(xref));

    const trailer = `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
    chunks.push(encoder.encode(trailer));

    const total = chunks.reduce((a, c) => a + c.length, 0);
    const out = new Uint8Array(total);
    let pos = 0;
    chunks.forEach((c) => { out.set(c, pos); pos += c.length; });
    return out;
  }

  private csvCell(value: any): string {
    const s = value == null ? '' : String(value);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }

  shareReport(): void {
    if (!this.shareSummary) { return; }
    const subject = encodeURIComponent(this.shareSummary.shareSubject);
    const body    = encodeURIComponent(this.shareSummary.shareBody);
    // A plain `mailto:` keeps the share fully client-side and avoids
    // routing report content through the backend mail pipeline. The
    // user's mail client picks up from there.
    const href = `mailto:?subject=${subject}&body=${body}`;
    if (typeof window !== 'undefined') {
      window.location.href = href;
    }
  }

  private load(): void {
    if (this.fetchSub) {
      this.fetchSub.unsubscribe();
    }
    this.loading = true;
    this.error = '';

    this.fetchSub = this.analyticsService
      .getReportsExportAnalytics(this.selectedPeriod, this.startDate, this.endDate)
      .subscribe(
        (data: ReportsExportAnalyticsResponse) => {
          this.reportSchedules = data.reportSchedules || [];
          this.shareSummary = data.shareSummary || null;
          // Publish to the per-tab export cache so the dashboard-level
          // Export button can serialise this tab if the user clicks it.
          this.analyticsService.setLatestTabData('reports-export', data);
          this.loading = false;
        },
        (err) => {
          this.error = (err && err.message) ? err.message : 'Failed to load Reports & Export data.';
          this.loading = false;
        },
      );
  }
}
